"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.scrollToRef = exports.getGetErrors = exports.getRemovePaths = exports.getHandleChange = exports.getRenderErrors = exports.getToggleEditMode = exports.getHandleSubmit = void 0;
const set_1 = __importDefault(require("lodash/set"));
const merge_1 = __importDefault(require("lodash/merge"));
const constants_1 = require("../constants");
const utils_1 = require("../utils");
const error_utils_1 = require("./error-utils");
function getHandleSubmit({ getErrors, updatedFieldsRef, setFormContext, formContext, props }) {
    return (e) => {
        e.preventDefault();
        const { allErrorsSet, updatedFieldsWithErrors } = getErrors();
        if (allErrorsSet.size) {
            // Send new errors to each field component so they can be rendered
            // under the input.
            updatedFieldsRef.current = updatedFieldsWithErrors;
            // Renders messages in allErrorsSet at the top of the form.
            setFormContext(Object.assign(Object.assign({}, formContext), { updatedFields: updatedFieldsRef.current, allErrorsSet }));
        }
        else {
            setFormContext(Object.assign(Object.assign({}, formContext), { isSubmitting: true }));
            const entityData = getUpdatedEntityData({
                formContext,
                props,
                updatedFieldsRef
            });
            const dataWithHiddenValues = appendHiddenFieldValues(formContext, entityData);
            props.onSubmit(dataWithHiddenValues, () => setFormContext(Object.assign(Object.assign({}, formContext), { isSubmitting: false, allErrorsSet: new Set() })));
        }
    };
}
exports.getHandleSubmit = getHandleSubmit;
/**
 * Looks for any fields where field.type === 'hidden'
 * and appends the input's value to entityData
 */
function appendHiddenFieldValues(formContext, entityData) {
    const hiddenFields = formContext.fields.filter((field) => field.type === constants_1.FIELD_TYPE_NAMES.HIDDEN);
    const hiddenElements = hiddenFields.map((field) => document.querySelector(`[name="${field.name || field.path}"]`));
    // a hidden field "name" attribute will equal config.name if provided,
    // and config.path as a fallback. hiddenFieldPathsByName links the
    // name or path with actual path.
    const hiddenFieldPathsByName = hiddenFields.reduce((acc, field) => {
        acc[field.name || field.path] = field.path;
        return acc;
    }, {});
    const hiddenValues = hiddenElements.reduce((acc, el) => {
        if ('name' in el && 'value' in el) {
            const path = hiddenFieldPathsByName[el.name];
            const dataObject = (0, set_1.default)({}, path, el.value);
            return (0, merge_1.default)({}, acc, dataObject);
        }
        return acc;
    }, {});
    return (0, merge_1.default)({}, entityData, hiddenValues);
}
/**
 * Creates a new version of entityData based on the
 * current updates.
 *
 * @param {Object} params
 * @param {Object} params.formContext
 * @param {Object} params.props
 * @param {Object} params.updatedFieldsRef React ref created via useRef
 * @returns {Object} updated entityData
 */
function getUpdatedEntityData({ formContext, props, updatedFieldsRef }) {
    // if defaultValue or defaultSelection is set, set those
    // within updatedFields if no other value exists.
    const updatedFieldsWithDefaults = formContext.fields.reduce((updatedFieldsWithDefaultValues, field) => field.setDefaultValue({
        entityData: props.entityData,
        updatedFields: updatedFieldsWithDefaultValues
    }), updatedFieldsRef.current);
    const updatedEntityData = (0, utils_1.addUpdatedFieldsToEntityData)(updatedFieldsWithDefaults, props.entityData);
    // Some field types may need to modify entityData before it is sent
    // to the consumer (ie ArrayField).
    const modifiedEntityData = formContext.fields.reduce((entityData, field) => {
        if (field.setEntityData) {
            return field.setEntityData({
                entityData,
                updatedFields: updatedFieldsWithDefaults
            });
        }
        return entityData;
    }, updatedEntityData);
    return modifiedEntityData;
}
function getToggleEditMode({ formContext, setFormContext }) {
    return () => {
        const fields = formContext.fields.map((field) => field.toggleEditMode());
        const configs = fields.reduce((acc, field) => (Object.assign(Object.assign({}, acc), field.getConfig())), {});
        // Clear any errors/updatedFields, and set new configs in formContext.
        setFormContext(Object.assign(Object.assign({}, formContext), { fields,
            configs, updatedFields: {}, allErrorsSet: new Set() }));
    };
}
exports.getToggleEditMode = getToggleEditMode;
function getRenderErrors(getErrors, updatedFieldsRef, setFormContext) {
    return () => {
        const { allErrorsSet, updatedFieldsWithErrors } = getErrors();
        if (allErrorsSet.size) {
            // Send new errors to each field component so they can be rendered
            // under the input.
            updatedFieldsRef.current = updatedFieldsWithErrors;
            // Renders messages in allErrorsSet at the top of the form.
            setFormContext((formCtx) => (Object.assign(Object.assign({}, formCtx), { updatedFields: updatedFieldsRef.current, allErrorsSet })));
        }
    };
}
exports.getRenderErrors = getRenderErrors;
function getHandleChange({ updatedFieldsRef, getErrors, props, formContext, setFormContext, renderErrors }) {
    return (payload) => {
        updatedFieldsRef.current = Object.assign(Object.assign({}, updatedFieldsRef.current), payload);
        setFormContext((ctx) => {
            if (props.hasConditionals) {
                return Object.assign(Object.assign({}, ctx), { fields: ctx.fields.map((field) => {
                        const hasDependency = Boolean(field.conditional);
                        if (!hasDependency)
                            return field;
                        // check in updated fields before defaulting to initial values
                        const valueToMatch = updatedFieldsRef.current[field.conditional.path]
                            ? updatedFieldsRef.current[field.conditional.path].value
                            : ctx.initialValues[field.conditional.path];
                        const meetsDependency = valueToMatch === field.conditional.value;
                        if (meetsDependency) {
                            field.isVisible = true;
                            field.isRequired = field.initialIsRequired;
                        }
                        else {
                            field.isVisible = false;
                            field.isRequired = false;
                            updatedFieldsRef.current = field.clearErrors(updatedFieldsRef.current);
                            if (ctx.allErrorsSet.has(field.isRequiredMsg)) {
                                const getErrors = getGetErrors({
                                    entityData: ctx.initialValues,
                                    fields: ctx.fields,
                                    updatedFieldsRef
                                });
                                setFormContext(Object.assign(Object.assign({}, ctx), { allErrorsSet: getErrors().allErrorsSet, updatedFields: updatedFieldsRef.current }));
                            }
                            if (updatedFieldsRef.current[field.path])
                                updatedFieldsRef.current[field.path].value = null;
                        }
                        return field;
                    }) });
            }
            return ctx;
        });
        const isValid = !getErrors().allErrorsSet.size;
        //When the disableSubmitWhenError prop is set to true, change isValid value if it has changed.
        setFormContext((ctx) => ctx.isValid !== isValid && props.disableSubmitWhenError
            ? Object.assign(Object.assign({}, ctx), { isValid }) : ctx);
        if (!props.onChange)
            return;
        const updatedEntityData = getUpdatedEntityData({
            formContext,
            // only include changes made by user, not full entityData object.
            // useful when consumer has multiple forms so data doesn't conflict
            props: Object.assign(Object.assign({}, props), { entityData: {} }),
            updatedFieldsRef
        });
        props.onChange({
            isValid,
            updatedFields: updatedFieldsRef.current,
            updatedEntityData,
            renderErrors
        });
    };
}
exports.getHandleChange = getHandleChange;
/**
 * Initialize a removePaths function that:
 * 1) Mutates updatedFieldsRef.current so it no longer contains the path/associated field
 * 2) Updates the updatedFields object in formContext to ensure useForm has the latest changes
 *
 * @param {Object} params
 * @param {Object} params.updatedFieldsRef React ref created via useRef
 * @param {Function} params.setFormContext
 * @returns {(paths: string[]) => void}
 */
function getRemovePaths({ updatedFieldsRef, setFormContext }) {
    /**
     * Function to remove path(s) from updatedFieldsRef
     * @param {string[]} keys
     */
    return (keys) => {
        const updatedFields = keys.reduce((updatedFields, key) => {
            const _a = updatedFields, 
            // eslint-disable-next-line no-unused-vars
            _b = key, 
            // eslint-disable-next-line no-unused-vars
            keyToRemove = _a[_b], updatedFieldsWithRemovedKeys = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
            return updatedFieldsWithRemovedKeys;
        }, updatedFieldsRef.current);
        updatedFieldsRef.current = updatedFields;
        // removing DOM elements can cause Hooks order to change,
        // which at times can cause a component to "disconnect" from
        // its state in useForm. this ensures updatedFields is up
        // to date as a backup in case that happens
        setFormContext((context) => (Object.assign(Object.assign({}, context), { updatedFields })));
    };
}
exports.getRemovePaths = getRemovePaths;
function getGetErrors({ entityData, fields, updatedFieldsRef }) {
    return () => {
        const getFieldErrors = (updatedFields, field) => field.getErrors({
            fields: [field],
            entityData: entityData,
            updatedFields
        });
        const updatedFieldsWithErrors = fields.reduce(getFieldErrors, updatedFieldsRef.current);
        return (0, error_utils_1.getAllErrors)(updatedFieldsWithErrors);
    };
}
exports.getGetErrors = getGetErrors;
/**
 * @param {Object} ref React ref created via useRef()
 */
function scrollToRef(ref) {
    if (ref.current && window) {
        window.scrollTo({
            top: ref.current.offsetTop,
            behavior: 'smooth'
        });
        ref.current.focus();
    }
}
exports.scrollToRef = scrollToRef;
//# sourceMappingURL=utils.js.map